/**
 * Accordion – plain JavaScript, no Bootstrap dependency.
 *
 * Supports:
 *   - independent mode: multiple items can be open at once
 *   - dependent mode: only one item open per accordion (data-bs-parent)
 *
 * HTML contract (Bootstrap class names kept for CSS compatibility):
 *   <div id="accordion-id" class="accordion">
 *     <div class="accordion-item">
 *       <h3 class="accordion-header">
 *         <a class="accordion-button collapsed"
 *            data-bs-toggle="collapse"
 *            data-bs-target="#collapseId"
 *            aria-expanded="false"
 *            aria-controls="collapseId">...</a>
 *       </h3>
 *       <div id="collapseId" class="accordion-collapse collapse">...</div>
 *     </div>
 *   </div>
 */

(function () {
	"use strict";

	/**
	 * Open a collapse panel and update the corresponding trigger button.
	 * Animates height from 0 to scrollHeight, then removes the inline style
	 * so the panel can grow freely if its content changes later.
	 *
	 * @param {Element} panel   - The .accordion-collapse element to open.
	 * @param {Element} trigger - The .accordion-button that controls the panel.
	 */
	function openPanel(panel, trigger) {
		panel.style.height = panel.scrollHeight + "px";
		panel.classList.add("show");
		panel.closest(".accordion-item")?.classList.add("accordion-item--open");
		trigger.classList.remove("collapsed");
		trigger.setAttribute("aria-expanded", "true");

		panel.addEventListener(
			"transitionend",
			function () {
				// Set to "auto" so the panel can grow if content changes dynamically.
				// Setting "" would fall back to the CSS rule (height: 0) and collapse.
				panel.style.height = "auto";
			},
			{ once: true },
		);
	}

	/**
	 * Close a collapse panel and update the corresponding trigger button.
	 * Pins the current pixel height first so the browser has a start value
	 * for the CSS transition, then animates to 0.
	 *
	 * @param {Element} panel   - The .accordion-collapse element to close.
	 * @param {Element} trigger - The .accordion-button that controls the panel.
	 */
	function closePanel(panel, trigger) {
		panel.style.height = panel.scrollHeight + "px";

		// Force reflow so the browser registers the explicit height
		// before we immediately change it to 0.
		panel.offsetHeight; // eslint-disable-line no-unused-expressions

		panel.style.height = "0";
		panel.classList.remove("show");
		panel.closest(".accordion-item")?.classList.remove("accordion-item--open");
		trigger.classList.add("collapsed");
		trigger.setAttribute("aria-expanded", "false");
	}

	/**
	 * Close all open sibling panels inside the same accordion (dependent mode).
	 *
	 * @param {string}  parentSelector - The CSS selector stored in data-bs-parent.
	 * @param {Element} skipPanel      - The panel that was just opened (keep it).
	 */
	function closeSiblings(parentSelector, skipPanel) {
		const accordion = document.querySelector(parentSelector);
		if (!accordion) return;

		accordion
			.querySelectorAll(".accordion-collapse.show")
			.forEach(function (openPanel) {
				if (openPanel === skipPanel) return;

				const openId = openPanel.getAttribute("id");
				const sibling = accordion.querySelector(
					'[data-bs-target="#' + openId + '"]',
				);

				closePanel(openPanel, sibling || openPanel);
			});
	}

	/**
	 * Handle a click on an accordion trigger button.
	 *
	 * @param {MouseEvent} event
	 */
	function onTriggerClick(event) {
		event.preventDefault();

		const trigger = event.currentTarget;
		const targetSelector = trigger.getAttribute("data-bs-target");
		if (!targetSelector) return;

		const panel = document.querySelector(targetSelector);
		if (!panel) return;

		const isOpen = panel.classList.contains("show");

		if (isOpen) {
			closePanel(panel, trigger);
			return;
		}

		// Close siblings first when in dependent (grouped) mode.
		const parentSelector = panel.getAttribute("data-bs-parent");
		if (parentSelector) {
			closeSiblings(parentSelector, panel);
		}

		openPanel(panel, trigger);
	}

	/**
	 * Initialise all accordion triggers on the page.
	 */
	function init() {
		document
			.querySelectorAll('[data-bs-toggle="collapse"]')
			.forEach(function (trigger) {
				trigger.addEventListener("click", onTriggerClick);
			});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
